import React from 'react';
import styled from 'styled-components';

import {
    CLICKABLE_ELEMENT_CSS_MIXIN,
    NON_HIGHLIGHTABLE_NOR_DRAGGABLE_ELEMENT_CSS_MIXIN,
} from '../../../style/styleConstants';
import { colors } from '../../../tokens/colors/colors';
import { InfoTooltip } from '../common/InfoTooltip';
import { font } from '../../../style/text';
import { getLocalizedTexts } from '../../../Locales';
import { logUserEventUtil } from '../../../lib/events/userEvents';
import { useApolloClient } from '@apollo/react-hooks';
import { TabType } from '../ProductDiscoveryPage';

const jokoLogo = '/assets/images/logos/logo-with-title.png';
const sidebar = '/assets/images/icons/sidebar.svg';

export function DesktopHeader({
    shouldShowConversationHistory,
    setShouldShowConversationHistory,
    shouldShowInfoTooltip,
    setShouldShowInfoTooltip,
    displayedTab,
    setDisplayedTab,
    setSelectedConversationHistoryItemId,
}: {
    shouldShowConversationHistory: boolean;
    setShouldShowConversationHistory: (value: boolean) => void;
    shouldShowInfoTooltip: boolean;
    setShouldShowInfoTooltip: (value: boolean) => void;
    displayedTab: TabType;
    setDisplayedTab: (value: TabType) => void;
    setSelectedConversationHistoryItemId: (value: string | undefined) => void;
}) {
    const apolloClient = useApolloClient();
    const texts = getLocalizedTexts().productDiscovery;
    return (
        <HeaderContainer>
            <LeftHeaderContainer>
                <HeaderImageContainer>
                    <HeaderImage
                        src={sidebar}
                        draggable={false}
                        onClick={() => setShouldShowConversationHistory(!shouldShowConversationHistory)}
                    />
                </HeaderImageContainer>
                <TabTitle
                    $isSelected={displayedTab === TabType.exploration}
                    onClick={() => {
                        setDisplayedTab(TabType.exploration);
                        logUserEventUtil(apolloClient, {
                            type: 'clickedExplorationTab',
                        });
                        setSelectedConversationHistoryItemId(undefined);
                    }}>
                    {texts.explorationTabTitle}
                </TabTitle>
                <TabTitle
                    $isSelected={displayedTab === TabType.favorites}
                    onClick={() => {
                        setDisplayedTab(TabType.favorites);
                        logUserEventUtil(apolloClient, { type: 'clickedFavoritesTab' });
                        setSelectedConversationHistoryItemId(undefined);
                    }}>
                    {texts.favoritesTab.title}
                </TabTitle>
            </LeftHeaderContainer>
            <LogoContainer>
                <JokoLogo src={jokoLogo} draggable={false} />
            </LogoContainer>
            <InfoTooltip {...{ shouldShowInfoTooltip, setShouldShowInfoTooltip }} />
        </HeaderContainer>
    );
}

const HeaderContainer = styled.div`
    display: flex;
    flex-direction: row;
    padding: 0px 16px;
    align-items: center;
    border-bottom: 1px solid ${colors.border.subtle};
    position: relative;
`;

const LeftHeaderContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;
    flex: 1;
`;

const LogoContainer = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
`;

export const HeaderImageContainer = styled.div`
    display: flex;
    width: 48px;
    height: 48px;
    align-items: center;
    justify-content: center;
    border-radius: 96px;
    ${CLICKABLE_ELEMENT_CSS_MIXIN}

    &:active {
        background-color: ${colors.background.subtle};
    }

    &:hover {
        background-color: ${colors.background.subtle};
        cursor: pointer;
    }
`;

export const HeaderImage = styled.img`
    width: 24px;
    height: 24px;
    padding: 12px;
    ${CLICKABLE_ELEMENT_CSS_MIXIN}
`;

const TabTitle = styled.div<{ $isSelected: boolean }>`
    padding: 16px 0px;
    border-bottom: 2px solid;
    border-color: ${({ $isSelected }) => ($isSelected ? colors.content.default : 'transparent')};
    font-family: ${font.ambitBold};
    font-size: 14px;
    color: ${({ $isSelected }) => ($isSelected ? colors.content.default : colors.content.secondary)};
    cursor: pointer;

    &:hover {
        ${({ $isSelected }) =>
            !$isSelected
                ? `
                  transition: border-color 0.2s ease-in-out;
                  color: ${colors.content.navigation};
                  border-color: ${colors.content.navigation};
             `
                : ''}
    }
`;

const JokoLogo = styled.img`
    width: 94px;
    object-fit: contain;
    ${NON_HIGHLIGHTABLE_NOR_DRAGGABLE_ELEMENT_CSS_MIXIN}
`;
